<template>
  <!--
    Atlas flyout — Vue 2.7 + Vuetify port of rm-portal-beta's
    AtlasFlyout.vue. Minimum viable shape: slide-in container, header,
    context chip, messages scroller, input bar. Wired to the singleton
    `useAtlasFlyout()` composable so the conversation persists across
    route navigation and modal lifecycle.

    `position: fixed` + z-index 2400 sits above Vuetify's v-dialog
    overlay (default ~200) so the flyout stays visible while a modal
    is open. `portal-vue` not used — would require an extra dep, and
    most rm-portal modals don't create transform-based stacking
    contexts that would trap a fixed element.

    Wrapping `<div class="atlas-flyout-root">` exists so the
    DocumentCompare modal can live as a SIBLING of the slide-in
    transition — closing the flyout shouldn't unmount the comparison
    grid, and Vue 2 still requires a single root element.

    Deferred for later iteration:
      - Resize handle (custom CSS, ~100 LOC)
      - Atlas Agents panel inside the flyout (chain + doc-creation
        strips collapsible card)
      - Jump-to-latest button + sticky-bottom auto-scroll heuristic
      - Streaming cursor + error banner
      - Voice input + attachments wired through ChatInput
  -->
  <div class="atlas-flyout-root">
  <!--
    Inline mode: the flyout fills its host container instead of sliding
    in from the right. Used when a host page embeds the chat panel
    inside its own layout (e.g. Document_Holder's "Atlas" right-panel
    tab). In that mode we always render — the host owns visibility via
    v-if on its tab — and we skip the resize handle, fullscreen
    top-offset measurement, and close button. The slide-in transition
    is still wrapped around the aside, but it never fires because the
    aside's v-if condition stays true for the whole lifecycle.
  -->
  <transition name="atlas-flyout-slide">
    <aside
      v-if="inline || (open && !globalHidden)"
      class="atlas-flyout"
      :class="{
        'atlas-flyout--resizing': resizing && !inline,
        'atlas-flyout--inline': inline,
      }"
      :style="inline ? null : { width: flyoutWidth + 'px', top: topOffset + 'px' }"
      role="complementary"
      aria-label="Atlas conversation"
    >
        <!--
          Left-edge resize handle. Full-height invisible hit target
          (12px wide, sits half-overlapping the flyout's left border).
          Visually anchored by:
            - a hairline separator that brightens on hover/drag
            - a centred grip pill with three dashes that fades in on
              hover, saturates to brand indigo while dragging
          Double-click resets to default width. Inline mode hides the
          handle — the host's tab is responsible for sizing.
        -->
        <div
          v-if="!inline"
          class="atlas-flyout__resizer"
          :class="{ 'atlas-flyout__resizer--active': resizing }"
          role="separator"
          aria-label="Resize Atlas flyout"
          aria-orientation="vertical"
          @mousedown="onResizeStart"
          @dblclick="resetWidth"
          @mousemove="onResizerTrack"
          @mouseleave="onResizerLeave"
        >
          <span
            class="atlas-flyout__resizer-line"
            :style="resizerLineStyle"
            aria-hidden="true"
          />
          <span
            v-if="resizerGripY != null"
            class="atlas-flyout__resizer-pill"
            :style="{ top: resizerGripY + 'px' }"
            aria-hidden="true"
          >
            <span class="atlas-flyout__resizer-dash" />
            <span class="atlas-flyout__resizer-dash" />
            <span class="atlas-flyout__resizer-dash" />
          </span>
        </div>

        <!-- Header strip: brand + close. Close hidden in inline mode —
             the host's tab UI owns dismissal. -->
        <div class="atlas-flyout__header">
          <!-- DS AtlasPanel mark: compass glyph in an indigo disc + plain
               "Atlas" title (Atlas keeps its own indigo identity). -->
          <div class="atlas-flyout__brand">
            <span class="atlas-flyout__mark" role="img" aria-label="Atlas">
              <v-icon size="15" color="white">explore</v-icon>
            </span>
            <span class="atlas-flyout__brand-text">Atlas</span>
          </div>
          <!--
            Context meter — centred in the header (the flyout's narrow layout
            has no room beside the send button, unlike the full-page chat).
          -->
          <AtlasUsageMeter
            :usage="contextUsage"
            :allowance="usageAllowance"
            class="atlas-flyout__meter"
          />
          <button
            v-if="!inline"
            type="button"
            class="atlas-flyout__close"
            aria-label="Close Atlas flyout"
            title="Close"
            @click="close"
          >
            <v-icon size="20">close</v-icon>
          </button>
        </div>

        <!-- Context chip — visible when a host page declares context. -->
        <div v-if="pageContext" class="atlas-flyout__context">
          <v-icon size="14" class="atlas-flyout__context-icon">mdi-crosshairs-gps</v-icon>
          <span class="atlas-flyout__context-label">In context</span>
          <span class="atlas-flyout__context-summary">{{ pageContext.summary }}</span>
        </div>

        <!--
          Previous conversations for the current page surface. Only
          renders when the host page declared a queryable context (today:
          documents) AND the current user has had earlier conversations
          against that surface. Same visual language as the Atlas Agents
          collapsible above the messages region.

          The list intentionally excludes the conversation that's
          currently open — switching INTO the same conversation would
          be a no-op, so we hide the row to keep the choices meaningful.
        -->
        <div
          v-if="contextFilter && previousConversations.length > 0"
          class="atlas-flyout__history"
        >
          <button
            type="button"
            class="atlas-flyout__history-toggle"
            :aria-expanded="historyOpen.toString()"
            @click="historyOpen = !historyOpen"
          >
            <v-icon size="16" class="atlas-flyout__history-chevron">
              {{ historyOpen ? 'mdi-chevron-down' : 'mdi-chevron-right' }}
            </v-icon>
            <span class="atlas-flyout__history-label">Previous conversations</span>
            <span class="atlas-flyout__history-count">
              {{ previousConversations.length }}
            </span>
          </button>
          <ul v-if="historyOpen" class="atlas-flyout__history-list">
            <li
              v-for="conv in previousConversations"
              :key="conv.conversation_id"
              class="atlas-flyout__history-item"
            >
              <button
                type="button"
                class="atlas-flyout__history-row"
                :title="conv.title"
                @click="onSwitchConversation(conv.conversation_id)"
              >
                <span class="atlas-flyout__history-title">{{ conv.title || 'Untitled' }}</span>
                <span
                  v-if="conv.last_message_preview"
                  class="atlas-flyout__history-preview"
                >
                  {{ conv.last_message_preview }}
                </span>
                <span class="atlas-flyout__history-meta">
                  {{ formatHistoryDate(conv.updated_date) }} · {{ conv.message_count }}
                </span>
              </button>
            </li>
          </ul>
        </div>

        <!--
          Atlas Agents section. Inline collapsibles mirroring
          rm-portal-beta's flyout: an outer "Atlas Agents" toggle, with
          two independently-collapsible cards inside (Prompt Chain
          Agent, Document Creation Agent). Each card surfaces a count +
          a working dot in its header so the user can read activity
          state without expanding.

          Section visibility is driven by `agentsSectionVisible` (any
          activity at all). Sub-panel `chainPanelOpen` / `docPanelOpen`
          auto-reopen when their pane FIRST appears, then respect the
          user's collapse choice until the pane empties + a new batch
          arrives — protects against the user collapsing a pile of
          completed strips and silently missing the next chain.
        -->
        <div
          v-if="agentsSectionVisible"
          class="atlas-flyout__agents"
        >
          <button
            type="button"
            class="atlas-flyout__agents-toggle"
            :aria-expanded="agentsSectionOpen.toString()"
            @click="agentsSectionOpen = !agentsSectionOpen"
          >
            <v-icon size="16" class="atlas-flyout__agents-chevron">
              {{ agentsSectionOpen ? 'mdi-chevron-down' : 'mdi-chevron-right' }}
            </v-icon>
            <span class="atlas-flyout__agents-label">Atlas Agents</span>
            <span
              class="atlas-flyout__dot-indicator"
              :class="agentsAnyWorking
                ? 'atlas-flyout__dot-indicator--working'
                : 'atlas-flyout__dot-indicator--idle'"
            />
            <span class="atlas-flyout__agents-summary">{{ agentsSummary }}</span>
          </button>

          <div v-if="agentsSectionOpen" class="atlas-flyout__agents-body">
            <!-- Prompt Chain Agent card -->
            <div
              v-if="panelChains.length > 0"
              class="atlas-flyout__agent-card atlas-flyout__agent-card--chain"
            >
              <button
                type="button"
                class="atlas-flyout__agent-card-toggle"
                :aria-expanded="chainPanelOpen.toString()"
                @click="chainPanelOpen = !chainPanelOpen"
              >
                <v-icon size="14" class="atlas-flyout__agents-chevron atlas-flyout__agents-chevron--chain">
                  {{ chainPanelOpen ? 'mdi-chevron-down' : 'mdi-chevron-right' }}
                </v-icon>
                <span class="atlas-flyout__agent-card-label atlas-flyout__agent-card-label--chain">
                  Prompt Chain Agent
                </span>
                <span
                  class="atlas-flyout__dot-indicator"
                  :class="chainAgentWorking
                    ? 'atlas-flyout__dot-indicator--working'
                    : 'atlas-flyout__dot-indicator--idle-chain'"
                />
                <span class="atlas-flyout__agent-card-count atlas-flyout__agent-card-count--chain">
                  {{ panelChains.length }}
                  {{ panelChains.length === 1 ? 'task' : 'tasks' }}
                </span>
              </button>
              <div v-if="chainPanelOpen" class="atlas-flyout__agent-strips">
                <ChainProgressStrip
                  v-for="chain in panelChains"
                  :key="chain.execution_id"
                  :progress="chain"
                  @jump-to="onJumpToMessage"
                  @dismiss="chat.dismissChain"
                />
              </div>
            </div>

            <!-- Document Creation Agent card -->
            <div
              v-if="readyToCreateList.length > 0 || panelDocCreations.length > 0"
              class="atlas-flyout__agent-card atlas-flyout__agent-card--doc"
            >
              <button
                type="button"
                class="atlas-flyout__agent-card-toggle"
                :aria-expanded="docPanelOpen.toString()"
                @click="docPanelOpen = !docPanelOpen"
              >
                <v-icon size="14" class="atlas-flyout__agents-chevron atlas-flyout__agents-chevron--doc">
                  {{ docPanelOpen ? 'mdi-chevron-down' : 'mdi-chevron-right' }}
                </v-icon>
                <span class="atlas-flyout__agent-card-label atlas-flyout__agent-card-label--doc">
                  Document Creation Agent
                </span>
                <span
                  class="atlas-flyout__dot-indicator"
                  :class="docAgentWorking
                    ? 'atlas-flyout__dot-indicator--working'
                    : 'atlas-flyout__dot-indicator--idle-doc'"
                />
                <span class="atlas-flyout__agent-card-count atlas-flyout__agent-card-count--doc">
                  {{ docPanelCountLabel }}
                </span>
              </button>

              <div v-if="docPanelOpen" class="atlas-flyout__agent-strips">
                <!-- Queued: chain finished, user hasn't kicked off creation yet -->
                <div
                  v-for="entry in readyToCreateList"
                  :key="`ready-${entry.execution_id}`"
                  class="atlas-flyout__queued"
                >
                  <div class="atlas-flyout__queued-row">
                    <v-icon size="13" color="#10B981">schedule</v-icon>
                    <span class="atlas-flyout__queued-name">
                      {{ entry.chain_name || `Execution #${entry.execution_id}` }}
                    </span>
                    <span class="atlas-flyout__queued-status">Queued</span>
                  </div>
                  <div class="atlas-flyout__queued-actions">
                    <button
                      v-if="entry.narrationMessageId"
                      type="button"
                      class="atlas-flyout__queued-link"
                      @click="onJumpToMessage(entry.narrationMessageId)"
                    >
                      <v-icon size="12">mdi-arrow-down</v-icon>
                      View
                    </button>
                    <span class="atlas-flyout__queued-actions-right">
                      <button
                        type="button"
                        class="atlas-flyout__queued-run"
                        :disabled="entry.kickedOff"
                        @click="onCreateDocument(entry.execution_id)"
                      >
                        <v-icon size="12">
                          {{ entry.kickedOff ? 'progress_activity' : 'play_arrow' }}
                        </v-icon>
                        {{ entry.kickedOff ? 'Starting…' : 'Run' }}
                      </button>
                      <button
                        type="button"
                        class="atlas-flyout__queued-dismiss"
                        @click="chat.dismissReadyToCreate(entry.execution_id)"
                      >
                        Dismiss
                      </button>
                    </span>
                  </div>
                </div>

                <!-- Active doc-creation strips -->
                <DocCreationProgressStrip
                  v-for="doc in panelDocCreations"
                  :key="`doc-${doc.doc_creation_request_id}`"
                  :progress="doc"
                  @jump-to="onJumpToMessage"
                  @dismiss="chat.dismissDocCreation"
                />
              </div>
            </div>
          </div>
        </div>

        <!--
          Messages region. Wraps the scroller in a relative-positioned
          container so the floating "jump to latest" pill can sit
          absolutely above the bottom edge without being inside the
          scroller (where it would scroll with the content).
        -->
        <div class="atlas-flyout__messages">
        <transition name="atlas-flyout-jump-fade">
          <!--
            Jump-to-latest button. Visible whenever the user has
            scrolled out of the stick-to-bottom zone — signals (a)
            they're not at the end, and (b) whether new content arrived
            while they were up there. Force-scroll on click;
            auto-hides once they return to the bottom.
          -->
          <button
            v-if="showJumpToLatest"
            type="button"
            class="atlas-flyout__jump-btn"
            title="Jump to latest message"
            @click="scrollToBottom(true)"
          >
            <v-icon size="14" color="white">mdi-arrow-down</v-icon>
            <span>{{ jumpToLatestLabel }}</span>
          </button>
        </transition>

        <div
          ref="scrollerRef"
          class="atlas-flyout__scroller"
          @scroll="onScrollerScroll"
        >
          <!-- Thread column (DS AtlasChat): capped at 42rem and centred so
               wide flyouts read as a document, with generous turn spacing. -->
          <div class="atlas-flyout__thread">
          <ChatMessage
            v-for="message in messages"
            :key="message.id"
            :message="message"
            :can-retry="!!message.isLatestAssistant && chatStatus === 'idle'"
            :ready-entry="readyEntryFor(message.chainExecutionId)"
            :doc-creation-in-progress="isDocCreationInProgress(message.chainExecutionId)"
            @ref-click="onRefClick"
            @follow-up="onFollowUp"
            @retry="onRetry"
            @create-document="onCreateDocument"
            :exit-workflow-key="defaultWorkflowKey"
            :parked-instance-id="parkedWorkflow ? parkedWorkflow.instance_id : null"
            @compare-documents="onCompareDocuments"
            @compare-levels="onCompareLevels"
            @open-summary="openDocumentSummary"
            @open-document="onOpenDocument"
          />

          <!--
            Composing bubble. Three layers, identical signal model to
            the full-page chat (vAgentChat.vue):
              1. Tool-call pills — one per `composing.toolCalls[]`,
                 e.g. "Searching jobs for 'engineer'…". Surfaced
                 ABOVE the bubble so the user sees what Atlas is
                 doing before/while it composes.
              2. Streaming-text bubble — `MarkdownContent` re-renders
                 on every token delta, with a blinking cursor at the
                 end so it visibly reads as live typing.
              3. Thinking-dots fallback — shown until the first text
                 delta lands, so the bubble isn't empty while the
                 model is just calling tools.
          -->
          <div v-if="composing" class="atlas-flyout__composing">
            <div class="atlas-flyout__composing-mark">
              <AtlasLogo :size="18" variant="brand" />
              <span class="atlas-flyout__composing-name">Atlas</span>
            </div>
            <div class="atlas-flyout__composing-content">
              <div
                v-if="composing.toolCalls && composing.toolCalls.length > 0"
                class="atlas-flyout__tool-pills"
              >
                <ToolCallIndicator
                  v-for="(call, i) in composing.toolCalls"
                  :key="i"
                  :call="call"
                />
              </div>

              <div v-if="streamingText" class="atlas-flyout__composing-bubble">
                <MarkdownContent :text="streamingText" @ref-click="onRefClick" />
                <span class="atlas-flyout__cursor" aria-hidden="true">▍</span>
              </div>
              <!-- While a prompt chain runs, show its live progress
                   (name, current step, bar) instead of "Thinking…". -->
              <div v-else-if="liveChain" class="atlas-flyout__composing-chain">
                <ChainProgressStrip :progress="liveChain" />
              </div>
              <div v-else class="atlas-flyout__composing-thinking">
                <span class="atlas-flyout__dots" aria-hidden="true">
                  <span class="atlas-flyout__dot" />
                  <span class="atlas-flyout__dot" />
                  <span class="atlas-flyout__dot" />
                </span>
                <span>Thinking…</span>
              </div>
            </div>
          </div>

          <!-- Workflow-owned chains that outlive their turn: live
               progress in the thread (the agents card skips these). -->
          <div
            v-for="c in inChatChains"
            :key="`inchat-${c.execution_id}`"
            class="atlas-flyout__inchat-chain"
          >
            <ChainProgressStrip :progress="c" @dismiss="chat.dismissChain" />
          </div>

          <!-- Workflow-owned DOCUMENT CREATIONS (the run after the
               content chain) — same in-thread treatment. -->
          <div
            v-for="d in inChatDocCreations"
            :key="`inchat-doc-${d.doc_creation_request_id}`"
            class="atlas-flyout__inchat-chain"
          >
            <DocCreationProgressStrip :progress="d" @dismiss="chat.dismissDocCreation" />
          </div>

          <!-- Handoff filler: a run finished but its follow-on (creation
               kickoff / created card) hasn't landed yet — bridges the
               ~10s of dead air after the chain strip completes. Rendered
               AS a strip (synthetic entry) so every status in the thread
               looks the same; pendingHandoff is false while any real
               strip is live, so there is only ever one. -->
          <div
            v-if="pendingHandoff && !composing"
            class="atlas-flyout__inchat-chain"
          >
            <DocCreationProgressStrip :progress="handoffStrip" />
          </div>

          <!-- Error banner. -->
          <div v-if="chatError" class="atlas-flyout__error">
            <v-icon size="14" color="error">error</v-icon>
            <span>{{ chatError }}</span>
          </div>
          </div> <!-- /.atlas-flyout__thread -->
        </div>
        </div> <!-- /.atlas-flyout__messages -->

        <!--
          Page-action chip. Renders when Atlas has attached a
          `page_action` envelope to its latest reply and the current
          route has a handler registered. Two display states:
            - status === 'pending'  → "Show results in current page?"
              with [Yes] [No] [Always] buttons.
            - status === 'applied'  → confirmation row; if it was
              auto-applied (preference = 'always') we offer a "Stop
              auto-applying" link that clears the preference without
              undoing the action itself.
          Sits above the input bar so it's always visible without the
          user scrolling — the action is about the latest turn.
        -->
        <div v-if="activeAction" class="atlas-flyout__action">
          <template v-if="activeAction.status === 'pending'">
            <div class="atlas-flyout__action-body">
              <v-icon size="16" class="atlas-flyout__action-icon">touch_app</v-icon>
              <span class="atlas-flyout__action-text">
                {{ activeAction.description || 'Display these results in the current page?' }}
              </span>
            </div>
            <div class="atlas-flyout__action-buttons">
              <button
                type="button"
                class="atlas-flyout__action-btn atlas-flyout__action-btn--primary"
                @click="respondToAction('yes')"
              >Yes</button>
              <button
                type="button"
                class="atlas-flyout__action-btn"
                @click="respondToAction('no')"
              >No</button>
              <button
                type="button"
                class="atlas-flyout__action-btn"
                @click="respondToAction('always')"
                title="Auto-apply future actions of this kind on this page"
              >Always</button>
            </div>
          </template>
          <template v-else-if="activeAction.status === 'applied'">
            <div class="atlas-flyout__action-body">
              <v-icon size="16" class="atlas-flyout__action-icon atlas-flyout__action-icon--applied">check_circle</v-icon>
              <span class="atlas-flyout__action-text">
                {{ activeAction.appliedAutomatically ? 'Applied to current page' : 'Applied' }}
              </span>
            </div>
            <div class="atlas-flyout__action-buttons">
              <button
                v-if="activeAction.appliedAutomatically"
                type="button"
                class="atlas-flyout__action-link"
                @click="respondToAction('stop-auto')"
              >Stop auto-applying</button>
              <button
                type="button"
                class="atlas-flyout__action-btn atlas-flyout__action-btn--ghost"
                aria-label="Dismiss"
                @click="respondToAction('dismiss')"
              >
                <v-icon size="14">close</v-icon>
              </button>
            </div>
          </template>
        </div>

        <!--
          Monthly-allowance banner. Amber from 80% of the client's Atlas
          budget, red at 100% (the server refuses turns; the input below
          is disabled too). Absent when no budget is configured.
        -->
        <div
          v-if="allowanceNotice"
          class="atlas-flyout__allowance"
          :class="{ 'atlas-flyout__allowance--blocked': isBudgetBlocked }"
        >
          <v-icon size="14" :color="isBudgetBlocked ? '#ffffff' : '#b45309'">warning</v-icon>
          <span>{{ allowanceNotice }}</span>
        </div>

        <!--
          Per-page controls. Active route declares these via
          `usePageControl()`; we render right above the input so the
          user can flip page-scoped settings (e.g. "Override Filters"
          on alljobs) without leaving the conversation flow. Empty on
          pages that haven't registered anything.
        -->
        <div
          v-if="pageControls.length > 0"
          class="atlas-flyout__controls"
        >
          <template v-for="control in pageControls">
            <!-- Toggle: small switch + label, anchored left. -->
            <label
              v-if="control.kind === 'toggle'"
              :key="control.id"
              class="atlas-flyout__control atlas-flyout__control--toggle"
              :title="control.description || control.label"
            >
              <input
                type="checkbox"
                :checked="!!control.modelValue.value"
                @change="control.modelValue.value = $event.target.checked"
              />
              <span>{{ control.label }}</span>
            </label>
            <!-- Button: text chip that fires the page's onClick. -->
            <button
              v-else-if="control.kind === 'button'"
              :key="control.id"
              type="button"
              class="atlas-flyout__control atlas-flyout__control--button"
              :title="control.description || control.label"
              @click="control.onClick && control.onClick()"
            >
              {{ control.label }}
            </button>
          </template>
        </div>

        <!--
          Parked-workflow chip: a workflow card sits superseded further
          up the thread. One click re-renders its current step as a
          fresh card at the bottom (the resume marker) — the visible
          twin of saying "continue" in chat.
        -->
        <div
          v-if="parkedWorkflow && chatStatus === 'idle' && !isBudgetBlocked"
          class="atlas-flyout__resume"
        >
          <button
            type="button"
            class="atlas-flyout__resume-btn"
            title="Bring the workflow's current step back as a fresh card"
            @click="onResumeWorkflow"
          >
            <v-icon size="14" color="primary">mdi-play-circle-outline</v-icon>
            {{ parkedWorkflow.label || 'Resume workflow' }}
          </button>
        </div>

        <!--
          Start chip: this page declares a context workflow, but the
          conversation has been used, so the fresh-only auto-start never
          fired (the portal carries ONE conversation across pages and
          surfaces). The explicit way in, without hijacking the thread.
        -->
        <div
          v-if="!parkedWorkflow && defaultWorkflowKey && canStartContextWorkflow && chatStatus === 'idle' && !isBudgetBlocked"
          class="atlas-flyout__resume"
        >
          <button
            type="button"
            class="atlas-flyout__resume-btn"
            title="Start this page's guided workflow"
            @click="onStartContextWorkflow"
          >
            <v-icon size="14" color="primary">mdi-play-circle-outline</v-icon>
            Start guided workflow
          </button>
        </div>

        <!-- Input bar. -->
        <div class="atlas-flyout__input">
          <ChatInput
            :disabled="chatStatus === 'sending' || chatStatus === 'initialising' || isBudgetBlocked"
            :placeholder="inputPlaceholder"
            :uploads="uploads"
            @submit="chat.sendMessage"
            @upload-files="onUploadFiles"
            @remove-upload="chat.removeUpload"
          />
          <!-- Governance line (DS AtlasChat footer, copy verbatim). -->
          <p class="atlas-flyout__foot">
            Atlas reads your architecture; it never changes it without your
            sign-off. Verify before publishing.
          </p>
        </div>
    </aside>
  </transition>

  <!--
    Fallback document-summary popover: pages that registered a
    `document.summary` handler own the popover themselves (dispatch
    path); on every OTHER page this instance makes reference chips
    still open a summary instead of dead-ending or falling back to a
    chat lookup.
  -->
  <DocViewPopUp
    class="atlas-flyout__summary-popup"
    :doc_id="fallbackSummary.doc_id"
    :trigger="fallbackSummary.trigger"
    @openDocument="onSummaryOpenDocument"
  />

  </div>
</template>

<script>
import { ref, computed, watch, nextTick, onMounted, onBeforeUnmount, getCurrentInstance } from 'vue';
import AtlasLogo from '@/components/agent/AtlasLogo.vue';
import ChatMessage from '@/components/agent/ChatMessage.vue';
import ChatInput from '@/components/agent/ChatInput.vue';
import MarkdownContent from '@/components/agent/MarkdownContent.vue';
import ToolCallIndicator from '@/components/agent/ToolCallIndicator.vue';
import ChainProgressStrip from '@/components/agent/ChainProgressStrip.vue';
import DocCreationProgressStrip from '@/components/agent/DocCreationProgressStrip.vue';
import AtlasUsageMeter from '@/components/agent/AtlasUsageMeter.vue';
import {
    useAtlasFlyout,
    dispatchPageAction,
    hasPageAction,
    saveScrollPosition,
    getScrollPosition,
} from '@/composables/agent/atlasFlyout';
import { resolveDocReference } from '@/composables/agent/resolveDocReference.js';
import DocViewPopUp from '@/components/common/DocViewPopUp.vue';
import { cleanStreamingText, deriveContextFilter } from '@rm-ui/agent/agentChat';
import { buildWorkflowStartMessage, buildWorkflowResumeMessage } from '@rm-ui/agent/uiBlockSpec';
import store from '@/store';

export default {
    name: 'AtlasFlyout',
    components: {
        AtlasLogo,
        ChatMessage,
        ChatInput,
        MarkdownContent,
        ToolCallIndicator,
        ChainProgressStrip,
        DocCreationProgressStrip,
        AtlasUsageMeter,
        DocViewPopUp,
    },
    props: {
        /**
         * Embed the flyout inline inside a host container instead of
         * letting it slide in from the right of the viewport. When
         * true the aside fills its parent (`position: absolute; inset:
         * 0`), the close button and resize handle are hidden, and the
         * `open` gate is bypassed — the host owns visibility via its
         * own `v-if`. Used by Document_Holder's "Atlas" tab.
         */
        inline: { type: Boolean, default: false },
    },
    // App.vue (global instance) / Document_Holder (inline instance)
    // open the standard document view for pages that registered no
    // `document.open` handler of their own.
    emits: ['openDocument'],
    setup(props, { emit }) {
        const flyout = useAtlasFlyout();
        const {
            chat,
            open,
            suppressed,
            globalHidden,
            pageContext,
            overlayOwnsContext,
            setPendingCreation,
            close,
            flyoutWidth,
            resizing,
            setFlyoutWidth,
            resetFlyoutWidth,
            setResizing,
            activeAction,
            respondToAction,
            pageControls,
        } = flyout;

        // Pull chat refs into local bindings so the template uses them
        // directly. Vue 2.7 templates auto-unwrap refs returned from
        // setup(), same as Vue 3.
        const messages = chat.messages;
        const composing = chat.composing;
        const chatStatus = chat.status;
        const chatError = chat.error;
        const uploads = chat.uploads;
        const contextUsage = chat.contextUsage;
        const usageAllowance = chat.usageAllowance;

        // Monthly-allowance affordances. Meter + banner only exist when
        // the client has an Atlas budget configured (allowance non-null);
        // `blocked` also disables the input — the server refuses turns
        // at 100% anyway, this just says so up front.
        const isBudgetBlocked = computed(() => !!usageAllowance.value?.blocked);
        // Context fill is causally linked to burn rate: the whole history
        // is re-processed on every call, so a fat conversation makes each
        // turn cost more. When the user is BOTH near the allowance and in
        // a long conversation, the warn banner names the cheapest fix.
        const CONTEXT_NUDGE_PCT = 60;
        const allowanceNotice = computed(() => {
            const a = usageAllowance.value;
            if (!a) return null;
            // a.scope names the binding budget: 'user' = this user's own cap.
            const whose = a.scope === 'user' ? 'your personal' : "this month's";
            if (a.blocked) {
                // EXPLICIT about the consequences: the input and the
                // workflow Resume affordance vanish together with this
                // state, and the amber→red flip alone is easy to miss
                // (user report, 2026-08-26).
                return a.scope === 'user'
                    ? 'Atlas is paused for you: your personal monthly usage allowance is fully used. ' +
                      'The chat input and workflow Resume stay disabled until it resets next month — ' +
                      'or an administrator raises your allowance.'
                    : "Atlas is paused: this month's usage allowance is fully used. " +
                      'The chat input and workflow Resume stay disabled until the allowance resets ' +
                      'next month — or an administrator raises the Atlas budget in client settings.';
            }
            if (a.warn) {
                const ctx = contextUsage.value;
                const ctxPct =
                    ctx && ctx.context_window
                        ? (ctx.total_tokens / ctx.context_window) * 100
                        : 0;
                return ctxPct >= CONTEXT_NUDGE_PCT
                    ? `You're approaching ${whose} Atlas usage allowance. Long conversations use it faster — starting a new chat resets the conversation size.`
                    : `You're approaching ${whose} Atlas usage allowance.`;
            }
            return null;
        });

        const scrollerRef = ref(null);

        /**
         * Drag-to-resize from the left edge. The flyout is anchored to
         * the right of the viewport, so dragging LEFT (negative cursor
         * delta) grows the width. We capture the starting cursor X +
         * starting width once at mousedown so the cursor tracks the
         * edge exactly without an offset jump.
         *
         * Width is clamped on every move + persisted to localStorage on
         * mouseup. Double-click on the handle resets to the default.
         */
        let _dragStartX = 0;
        let _dragStartWidth = 0;

        function onResizeMove(ev) {
            const delta = _dragStartX - ev.clientX;
            setFlyoutWidth(_dragStartWidth + delta);
        }
        function onResizeEnd() {
            setResizing(false);
            window.removeEventListener('mousemove', onResizeMove);
            window.removeEventListener('mouseup', onResizeEnd);
            // Persist the final width at drag end (we skip persisting on
            // every mousemove to avoid hammering localStorage).
            setFlyoutWidth(flyoutWidth.value, { persist: true });
        }
        function onResizeStart(ev) {
            ev.preventDefault();
            setResizing(true);
            _dragStartX = ev.clientX;
            _dragStartWidth = flyoutWidth.value;
            window.addEventListener('mousemove', onResizeMove);
            window.addEventListener('mouseup', onResizeEnd);
        }
        function resetWidth() {
            resetFlyoutWidth();
        }

        /**
         * Cursor-following grip (DS AtlasPanel rail treatment). The grip
         * pill rides the cursor along the rail, and the hairline renders
         * as a ~140px gradient segment centred on it rather than a
         * full-height line with a fixed centre pill. Cleared on
         * mouseleave unless a drag is in flight — mid-drag the cursor
         * routinely leaves the 12px rail, and the grip should hold its
         * last position rather than vanish.
         */
        const resizerGripY = ref(null);
        const resizerRailH = ref(0);
        function onResizerTrack(ev) {
            const rect = ev.currentTarget.getBoundingClientRect();
            resizerRailH.value = rect.height;
            resizerGripY.value = Math.min(
                rect.height - 30,
                Math.max(30, ev.clientY - rect.top),
            );
        }
        function onResizerLeave() {
            if (!resizing.value) resizerGripY.value = null;
        }
        const resizerLineStyle = computed(() => {
            if (resizerGripY.value == null) return null;
            const top = Math.max(16, resizerGripY.value - 70);
            const bottom = Math.min(
                resizerRailH.value - 16,
                resizerGripY.value + 70,
            );
            return { top: top + 'px', height: Math.max(0, bottom - top) + 'px' };
        });

        // Reclamp the persisted width if the user shrinks their browser
        // window after a drag — keeps the flyout from suddenly being
        // wider than its viewport allows.
        function onViewportResize() {
            setFlyoutWidth(flyoutWidth.value);
        }

        /**
         * Top offset — distance from the viewport top to where the
         * flyout starts. Computed dynamically so the flyout fits flush
         * against whichever toolbar is currently on top:
         *   - In normal navigation, that's rm-portal's main app bar
         *     (typically ~48px in dense mode).
         *   - When a fullscreen v-dialog (e.g. the document modal) is
         *     open, the dialog renders its OWN v-app-bar on top — that
         *     toolbar is taller (~64px) and sits inside the dialog's
         *     fixed-positioned container.
         *
         * Hardcoding `top: 56px` doesn't fit either case cleanly, so
         * we measure the topmost visible toolbar and track changes via
         * MutationObserver (catches dialog open/close) + window resize.
         */
        const topOffset = ref(64);

        /**
         * Find the toolbar inside an active fullscreen dialog — when
         * one's open it covers the main app bar so its inner toolbar's
         * bottom is the right reference for the flyout.
         */
        function findFullscreenDialogToolbar() {
            const dialogs = document.querySelectorAll('.v-dialog--fullscreen');
            for (const dialog of dialogs) {
                if (dialog.offsetParent === null) continue;
                const inner = dialog.querySelector('.v-app-bar, .v-toolbar');
                if (inner && inner.offsetParent !== null) {
                    const rect = inner.getBoundingClientRect();
                    if (rect.top <= 50 && rect.height >= 20) return inner;
                }
            }
            return null;
        }

        /**
         * Pick something to attach a ResizeObserver to, so we
         * recompute when its layout changes. Prefer the fullscreen
         * dialog's toolbar (when present), otherwise the main `<main
         * class="v-main">` whose padding-top tracks the app-bar
         * height.
         */
        function findActiveToolbar() {
            return (
                findFullscreenDialogToolbar() ||
                document.querySelector('main.v-main') ||
                document.querySelector('header.v-app-bar')
            );
        }

        /**
         * Compute the flyout's top offset.
         *
         * Strategy in priority order:
         *   1. If a fullscreen dialog is active (e.g. the document
         *      modal) → use its inner toolbar's bottom edge.
         *   2. Otherwise → read `<main class="v-main">`'s computed
         *      `padding-top`. Vuetify sets this to exactly the height
         *      of all `app`-mode toolbars combined (including any
         *      extensions), which is precisely where the page content
         *      starts and therefore where the flyout should too. This
         *      is more robust than measuring the v-app-bar directly
         *      because it handles extensions, multiple stacked bars,
         *      and the `dense`/`prominent` modifiers identically.
         *   3. Last resort → measure `<header class="v-app-bar">`
         *      directly. Falls back to 64 if everything fails.
         */
        function computeTopOffset() {
            // 1. Fullscreen modal.
            const dialogToolbar = findFullscreenDialogToolbar();
            if (dialogToolbar) {
                topOffset.value = Math.max(
                    0,
                    Math.round(dialogToolbar.getBoundingClientRect().bottom),
                );
                return;
            }
            // 2. v-main padding-top — Vuetify's authoritative signal
            //    for "where page content starts beneath all app bars".
            const main = document.querySelector('main.v-main');
            if (main) {
                const padTop = parseFloat(getComputedStyle(main).paddingTop);
                if (Number.isFinite(padTop) && padTop > 0) {
                    topOffset.value = Math.round(padTop);
                    return;
                }
            }
            // 3. Direct measurement of the app bar, then a safe default.
            const bar = document.querySelector('header.v-app-bar');
            if (bar && bar.offsetParent !== null) {
                topOffset.value = Math.max(
                    0,
                    Math.round(bar.getBoundingClientRect().bottom),
                );
                return;
            }
            topOffset.value = 64;
        }

        let _domObserver = null;
        let _toolbarObserver = null;
        let _currentToolbar = null;

        /**
         * Bind a ResizeObserver to whichever toolbar is currently
         * active. Re-attached whenever the active toolbar element
         * changes (e.g. dialog opens/closes, route navigation swaps the
         * page out from underneath an embedded toolbar).
         */
        function attachToolbarObserver() {
            const el = findActiveToolbar();
            if (el === _currentToolbar) return;
            if (_toolbarObserver) {
                _toolbarObserver.disconnect();
                _toolbarObserver = null;
            }
            _currentToolbar = el;
            if (el && typeof ResizeObserver !== 'undefined') {
                _toolbarObserver = new ResizeObserver(() => computeTopOffset());
                _toolbarObserver.observe(el);
            }
        }

        function recompute() {
            attachToolbarObserver();
            computeTopOffset();
        }

        onMounted(() => {
            recompute();
            // MutationObserver on body catches v-dialog open/close +
            // route-driven DOM swaps. Filtered to class attribute +
            // childList so we don't fire on every text/style mutation.
            _domObserver = new MutationObserver(recompute);
            _domObserver.observe(document.body, {
                attributes: true,
                childList: true,
                subtree: true,
                attributeFilter: ['class'],
            });
            window.addEventListener('resize', computeTopOffset);
            window.addEventListener('resize', onViewportResize);
            // Remount-aware scroll restore. After a context switch
            // unmounts then remounts this flyout, the scroller is a
            // fresh DOM node sitting at scrollTop=0. If we have a
            // saved position for the current conversation, apply it
            // so the user lands where they left off instead of at
            // the top.
            restoreScrollPosition();
        });
        onBeforeUnmount(() => {
            _domObserver?.disconnect();
            _toolbarObserver?.disconnect();
            window.removeEventListener('resize', computeTopOffset);
            window.removeEventListener('resize', onViewportResize);
            // Defensive: if the user releases mouse outside the
            // window, the listeners we attached during a drag would
            // leak. Always remove them on unmount.
            window.removeEventListener('mousemove', onResizeMove);
            window.removeEventListener('mouseup', onResizeEnd);
        });
        // Recompute when the flyout toggles open — covers cases where
        // the toolbar layout changed while the flyout was hidden (e.g.
        // user opens a modal, closes the flyout, re-opens it later).
        watch(() => flyout.open.value, (isOpen) => {
            if (isOpen) nextTick(() => computeTopOffset());
        });

        // Vue Router 3.x route changes don't always produce DOM
        // mutations the MutationObserver picks up — particularly when
        // the same v-app-bar stays mounted across routes but its meta-
        // driven height changes. Watching the route directly via
        // getCurrentInstance().proxy.$route guarantees a recompute on
        // navigation. Block-form arrow + explicit `void` so the lint
        // rule (`vue/valid-next-tick`) sees the Promise as discarded
        // rather than implicitly returned.
        const instance = getCurrentInstance();
        watch(
            () => instance?.proxy?.$route?.fullPath,
            () => {
                void nextTick(() => recompute());
            },
        );

        // Detect the global Vue Portal/Teleport mount target. rm-portal
        // doesn't have @vue/portal-target installed; fall back to native
        // Vue 2.7 `<portal>` if vue2-portal is available. If neither is
        // available the flyout still renders inline (the App.vue mount
        // point is high enough in the tree that escaping stacking
        // contexts is rarely needed for the basic chat).
        const hasPortal = ref(false);
        // Set on mount via the component-options hook (no portal target
        // available without an extra dep — leave as false for now and
        // render inline at App.vue level).
        // TODO: add vue2-portal if we hit z-index issues with v-dialogs.

        const inputPlaceholder = computed(() => {
            const ctx = pageContext.value;
            if (!ctx) return 'Ask Atlas…';
            if (ctx.kind === 'document') return 'Ask about this document…';
            if (ctx.kind === 'skills') return 'Ask about this skills view…';
            return `Ask about this ${ctx.kind}…`;
        });

        // ── Context workflow auto-start ─────────────────────────────
        // Pages can declare an `atlas_context_workflow` route-meta
        // override (Admin → Page Permissions → Meta Admin): when the
        // flyout is OPEN with a fresh conversation on such a page, we
        // kick the configured guided workflow off automatically via the
        // [workflow:start] marker. Gated on the client's
        // atlas_workflows setting so a disabled client never auto-sends.
        // The route's context workflow (feature-gated) — the auto-start
        // key, and what the workflow cards' Exit action bounces back to.
        function contextWorkflowKey() {
            // A document (or any modal) opens OVER the current route, so
            // route.meta still names the underlying page's workflow.
            // Starting it here would hijack a chat the user opened to ask
            // about the document in front of them.
            if (overlayOwnsContext.value) return null;
            const route = instance?.proxy?.$route;
            const workflowKey = route?.meta?.atlas_context_workflow;
            if (!workflowKey) return null;
            const settings = instance?.proxy?.$loginState?.user?.settings || [];
            const enabled = settings.some(
                (s) => s.setting === 'atlas_workflows' && s.value === 'true',
            );
            return enabled ? workflowKey : null;
        }
        chat.autoStart = () => {
            const workflowKey = contextWorkflowKey();
            return workflowKey ? buildWorkflowStartMessage(workflowKey) : null;
        };
        // Same key, second consumer: /agent/init sends it as
        // context_workflow so a fresh conversation opens with the
        // workflow's authored intro message (Admin → Atlas Workflows →
        // Intro message) instead of the generic greeting.
        chat.contextWorkflow = () => contextWorkflowKey();
        const defaultWorkflowKey = computed(() => contextWorkflowKey());
        watch(
            [open, chat.status, chat.conversationId],
            ([isOpen, status]) => {
                if (isOpen && status === 'idle') chat.maybeAutoStart();
            },
        );

        // A successful single-document creation opens the new document
        // for review via the host page's standard document.open handler
        // (same wiring as vAgentChat — whichever host mounted last owns
        // the singleton hook, and both do the same dispatch), falling
        // back to the host's standard document view on other pages.
        chat.onDocumentsCreated = async (docs) => {
            if (!Array.isArray(docs) || docs.length !== 1) return;
            // A guided workflow may have ADDED the job title to the
            // hierarchy on the way in, so the cached tree can be a step
            // behind — the document would then render its raw hr_id under
            // "Unclassified". Same refresh every other save path does.
            await store.dispatch('hierarchies/refresh');
            if (docs[0]?.reference) {
                setPendingCreation(
                    docs[0].reference,
                    `I've created ${docs[0].reference}${docs[0].doc_name ? ` — ${docs[0].doc_name}` : ''}. ` +
                        'Ask me to change any part of it, regenerate a section, or compare it with similar roles.',
                );
            }
            if (!dispatchPageAction('document.open', { doc: docs[0] })) {
                emit('openDocument', docs[0]);
            }
        };

        const streamingText = computed(() =>
            composing.value ? cleanStreamingText(composing.value.text) : '',
        );

        function readyEntryFor(executionId) {
            if (executionId == null) return null;
            return chat.readyToCreate.value[executionId] ?? null;
        }
        function isDocCreationInProgress(executionId) {
            if (executionId == null) return false;
            return Object.values(chat.docCreationProgress.value).some(
                (d) => d.execution_id === executionId,
            );
        }

        /**
         * Sticky-to-bottom auto-scroll heuristic. When a new message
         * lands or composing text grows, only pull the user to the
         * latest if they were ALREADY at (or close to) the bottom.
         * If they scrolled up to read a long response we leave them
         * where they are — otherwise streamed replies become
         * unreadable as the cursor yanks them to the end on every
         * token. The 40px threshold is forgiving enough that natural
         * micro-scrolls in the reading zone still count as "at bottom".
         *
         * Pair: `showJumpToLatest` floats a pill above the input so
         * the user can opt INTO the jump when they want.
         */
        const STICK_THRESHOLD = 40;
        const scrolledUp = ref(false);
        const hasUnreadBelow = ref(false);

        function isNearBottom() {
            const el = scrollerRef.value;
            if (!el) return true;
            const distance = el.scrollHeight - el.scrollTop - el.clientHeight;
            return distance <= STICK_THRESHOLD;
        }
        function scrollToBottom(force = false) {
            nextTick(() => {
                const el = scrollerRef.value;
                if (!el) return;
                if (!force && !isNearBottom()) return;
                el.scrollTop = el.scrollHeight;
                // Reset signals once we're definitely at the bottom.
                hasUnreadBelow.value = false;
                scrolledUp.value = false;
            });
        }
        function onScrollerScroll() {
            const near = isNearBottom();
            scrolledUp.value = !near;
            if (near) hasUnreadBelow.value = false;
            // Remember where the user is in THIS conversation so the
            // next time we mount (after a context switch / modal open
            // & close) we can restore them to the same spot rather
            // than dropping them at the top.
            const el = scrollerRef.value;
            const convId = chat.conversationId.value;
            if (el && convId) {
                saveScrollPosition(convId, {
                    scrollTop: el.scrollTop,
                    wasAtBottom: near,
                });
            }
        }

        /**
         * Restore scroll position for the currently-active
         * conversation, if one has been saved. Called after mount and
         * after a conversationId change so a remounted flyout (or a
         * conv swap inside one mount) lands on the same row the user
         * left. `wasAtBottom` wins over the numeric scrollTop because
         * the conversation may have grown while the user was away —
         * sticking to the LIVE bottom is what they actually want.
         *
         * Wrapped in `nextTick` so messages have rendered before we
         * read `scrollHeight`. Returns true if a saved position was
         * applied, false otherwise (callers can fall back to default
         * scroll-to-bottom).
         */
        function restoreScrollPosition() {
            const convId = chat.conversationId.value;
            const saved = getScrollPosition(convId);
            if (!saved) return false;
            nextTick(() => {
                const el = scrollerRef.value;
                if (!el) return;
                if (saved.wasAtBottom) {
                    el.scrollTop = el.scrollHeight;
                } else {
                    el.scrollTop = saved.scrollTop;
                }
            });
            return true;
        }

        const showJumpToLatest = computed(() => scrolledUp.value);
        const jumpToLatestLabel = computed(() =>
            hasUnreadBelow.value ? 'New messages' : 'Jump to latest',
        );

        // A new message is "something new happened" — but we still
        // respect the user's scroll. Use the non-forced scroll path
        // so we only follow when they're already near the bottom.
        // When they're NOT, flag that there's unread content so the
        // jump button labels itself accordingly.
        //
        // Exception: when the user themselves just sent a message,
        // ALWAYS pull to the bottom. They committed the action, their
        // bubble must be visible — leaving them scrolled up reading
        // older context after they typed "what about X?" reads as the
        // input being broken.
        watch(() => messages.value.length, () => {
            const last = messages.value[messages.value.length - 1];
            const isOwnMessage = last?.role === 'user';
            scrollToBottom(isOwnMessage);
            if (!isOwnMessage && !isNearBottom()) hasUnreadBelow.value = true;
        });
        // Composing text growth: same sticky behaviour. Deep watch so
        // we react to text deltas inside the composing object, not
        // just its identity.
        watch(
            () => composing.value,
            (val) => { if (val) scrollToBottom(); },
            { deep: true },
        );
        // Opening the flyout IS an explicit "take me there" action —
        // force-scroll regardless of prior position.
        watch(open, (isOpen) => {
            if (isOpen) scrollToBottom(true);
        });
        // Results returning (status leaving 'sending') is an explicit
        // "the answer just arrived" moment — force-scroll so the user
        // lands on the final reply regardless of where they drifted to
        // while it was streaming.
        watch(chatStatus, (next, prev) => {
            if (prev === 'sending' && next !== 'sending') {
                scrollToBottom(true);
            }
        });
        // Mid-life conversation switch (e.g. user picks a previous
        // conversation from the history dropdown, or the context
        // changes without remounting the flyout). Restore the saved
        // scroll position for the new conversation; falls through to
        // the existing message-watcher's scrollToBottom() when no
        // saved position exists.
        watch(
            () => chat.conversationId.value,
            (newId, oldId) => {
                if (!newId || newId === oldId) return;
                restoreScrollPosition();
            },
        );

        // Click handlers — same pattern as the full-page chat.
        /**
         * Reference-chip click → document SUMMARY, never a chat lookup:
         * chips already carrying a doc_id short-circuit in ChatMessage;
         * bare references land here and are resolved server-side
         * (permission-checked) before opening the popover. Only when
         * resolution fails does the click degrade to the conversational
         * "Tell me about…" turn.
         */
        async function onRefClick(refData, event) {
            const isObj = refData && typeof refData === 'object';
            const reference = isObj ? refData.reference : refData;
            if (!reference) return;
            if (isObj && refData.doc_id != null && event) {
                openDocumentSummary(event, refData);
                return;
            }
            const doc = await resolveDocReference(reference);
            if (doc && event) {
                openDocumentSummary(event, doc);
                return;
            }
            if (chatStatus.value === 'sending') return;
            chat.sendMessage(`Tell me about ${reference}`);
        }
        function onFollowUp(question) {
            if (chatStatus.value === 'sending') return;
            chat.sendMessage(question);
        }
        /**
         * "Resume workflow" chip: visible while a workflow card sits
         * superseded up-thread (chat.parkedWorkflow). Sends the resume
         * marker — the engine re-renders the current step as a fresh
         * card at the bottom, so nobody scrolls back.
         */
        const parkedWorkflow = chat.parkedWorkflow;
        function onResumeWorkflow() {
            if (chatStatus.value === 'sending') return;
            const parked = parkedWorkflow.value;
            if (!parked) return;
            chat.sendMessage(
                buildWorkflowResumeMessage(parked.instance_id, parked.step_key, parked.label),
            );
        }
        /** "Start guided workflow" chip — the used-conversation twin of
         * the fresh-conversation auto-start. */
        const canStartContextWorkflow = chat.canOfferWorkflowStart;
        function onStartContextWorkflow() {
            if (chatStatus.value === 'sending') return;
            const workflowKey = contextWorkflowKey();
            if (!workflowKey) return;
            chat.sendMessage(buildWorkflowStartMessage(workflowKey));
        }
        function onRetry() {
            if (chatStatus.value === 'sending') return;
            chat.retryLast();
        }
        function onCreateDocument(executionId) {
            chat.createDocumentFromChain(executionId).catch((err) => {
                console.error('[AtlasFlyout] createDocumentFromChain failed', err);
            });
        }

        /**
         * Forward user-initiated chat-UI actions to the host page. The
         * flyout used to own its own DocumentCompare + DocViewPopUp,
         * but that duplicated modal logic the route component already
         * has — and meant the flyout had to know what every action
         * "means". Now we just dispatch the payload by kind and let
         * whichever page is mounted decide. Pages register handlers
         * via `usePageAction(kind, handler)`; ChatMessage hides the
         * matching CTA when no handler exists, so a click here always
         * lands somewhere.
         *
         * Flyout stays OPEN after dispatch — the user is mid-conversation
         * and may want to keep iterating ("now compare these other two")
         * alongside the compare modal. Page-side modals must handle their
         * own z-index against the flyout rather than relying on us
         * vacating the screen.
         */
        function onCompareDocuments(items) {
            if (!Array.isArray(items) || items.length < 2) return;
            dispatchPageAction('document.compare', items);
        }
        // Pages with a registered `document.summary` handler own the
        // popover (grids anchor it to their own layout); everywhere else
        // the flyout's fallback DocViewPopUp keeps summaries working.
        const fallbackSummary = ref({ doc_id: null, trigger: null });
        const summaryHandlerRegistered = hasPageAction('document.summary');
        function openDocumentSummary(event, item) {
            if (summaryHandlerRegistered.value) {
                dispatchPageAction('document.summary', { event, doc: item });
                return;
            }
            if (!item || item.doc_id == null || !event) return;
            // Wholesale reassign so DocViewPopUp's trigger watcher fires
            // even when the same doc is reopened.
            fallbackSummary.value = { doc_id: Number(item.doc_id), trigger: event };
        }
        async function onOpenDocument(doc) {
            // See vAgentChat's copy: a card's Open must wait for the
            // hierarchy tree, or a just-created job title renders as its
            // raw hr_id under "Unclassified".
            await store.dispatch('hierarchies/refresh');
            // See vAgentChat: a post-creation card's note opens the panel
            // on the document with what Atlas just made.
            if (doc?.created_note && doc?.reference) {
                setPendingCreation(doc.reference, doc.created_note);
            }
            // Pages that registered document.open own the open (their
            // wiring may do more than show the doc); everywhere else the
            // host (App.vue / Document_Holder) opens the standard
            // document view — so "Open" never leaves the app.
            if (!dispatchPageAction('document.open', { doc })) {
                emit('openDocument', doc);
            }
        }
        // Compare levels (PD-1211) — dispatched to the host grid, which
        // opens the standalone levelling comparison seeded with these
        // evaluated jobs.
        function onCompareLevels(items) {
            if (!Array.isArray(items) || items.length < 2) return;
            dispatchPageAction('document.compare-levels', items);
        }
        /**
         * "Open" from within the fallback summary popover. Forwards to the
         * host page's document.open handler where one exists; elsewhere
         * the host opens the standard document view via the emit.
         */
        function onSummaryOpenDocument(doc) {
            if (!doc || doc.doc_id == null) return;
            if (!dispatchPageAction('document.open', { doc })) {
                emit('openDocument', doc);
            }
        }
        function onUploadFiles(files) {
            for (const file of files) chat.uploadFile(file);
        }

        // ── Atlas Agents collapsibles ──────────────────────────
        //
        // Sorted lists of in-flight chain executions and document-
        // creation requests. Sorting by id keeps the strips stacked
        // in kickoff order so the user can pair a strip with the
        // message that started it.
        const activeChains = computed(() =>
            Object.values(chat.chainProgress.value).sort(
                (a, b) => a.execution_id - b.execution_id,
            ),
        );
        // The most recent still-running chain — swapped in for the
        // "Thinking…" label in the composing indicator so the user sees
        // what's actually taking the time.
        const liveChain = computed(() => {
            const running = activeChains.value.filter(
                (e) => !['completed', 'failed', 'awaiting_selection', 'timed_out'].includes(e.status),
            );
            return running.length ? running[running.length - 1] : null;
        });
        // What the agents card actually lists: everything EXCEPT chains
        // the chat is already showing — the one live in the composing
        // indicator, and inline runs that finished within their turn
        // (dismissOnFinalise; their next card rides the same response).
        const panelChains = computed(() =>
            activeChains.value.filter((c) => {
                if (c.dismissOnFinalise === true) return false;
                // Workflow-owned runs render their lifecycle in the chat
                // (in-chat strip) — no panel duplicate.
                if (c.in_chat === true) return false;
                if (
                    chat.composing.value &&
                    liveChain.value &&
                    c.execution_id === liveChain.value.execution_id
                ) {
                    return false;
                }
                return true;
            }),
        );
        // Live workflow-owned chains shown IN the chat once their
        // kickoff turn has returned (e.g. a create-form's content
        // generation, detached from the HTTP turn). While composing the
        // composing indicator covers it; terminal entries vanish —
        // completion arrives as narration/cards in the thread.
        const inChatChains = computed(() => {
            if (chat.composing.value) return [];
            // Live runs AND dead ends stay visible: a run whose process
            // died (server restart → reaped, or locally timed out by the
            // staleness ticker) posts no narration, so hiding its failed
            // strip would erase the only evidence it ended. Completed
            // runs hide — their card/narration lands in the thread.
            return activeChains.value.filter(
                (c) =>
                    c.in_chat === true &&
                    !['completed', 'awaiting_selection'].includes(c.status),
            );
        });
        const activeDocCreations = computed(() =>
            Object.values(chat.docCreationProgress.value).sort(
                (a, b) => a.doc_creation_request_id - b.doc_creation_request_id,
            ),
        );
        // Chat-driven creations (doc_upload confirm) narrate their whole
        // lifecycle in the conversation — the panel lists only the rest.
        const panelDocCreations = computed(() =>
            activeDocCreations.value.filter((d) => d.in_chat !== true),
        );
        // Workflow-owned document creations, in-thread — the run that
        // follows the content chain (chain strip completes, creation
        // takes ~10s, then the created card posts). Same rules as
        // inChatChains.
        const inChatDocCreations = computed(() => {
            if (chat.composing.value) return [];
            // Same dead-end rule as inChatChains: only Complete hides
            // (its created card lands in the thread).
            return activeDocCreations.value.filter(
                (d) => d.in_chat === true && d.status !== 'Complete',
            );
        });
        // Synthetic strip entry for the handoff filler: non-terminal
        // status → spinning icon + indeterminate bar, no actions —
        // visually identical to the real strips it bridges between.
        const handoffStrip = {
            doc_creation_request_id: 'handoff',
            chain_name: 'Finalising document',
            status: 'Finalising',
            progress: { total: 0, complete: 0, failed: 0, percentage: 0 },
        };
        // Gap filler between a run's success and its follow-on landing —
        // see agentChat.pendingHandoff.
        const pendingHandoff = chat.pendingHandoff;
        const readyToCreateList = computed(() =>
            Object.values(chat.readyToCreate.value).sort(
                (a, b) => a.execution_id - b.execution_id,
            ),
        );

        // Visible whenever there's any activity (running, queued, or
        // post-terminal pinned). Auto-collapses when everything's gone.
        const agentsSectionVisible = computed(
            () =>
                panelChains.value.length > 0 ||
                panelDocCreations.value.length > 0 ||
                readyToCreateList.value.length > 0,
        );

        // Which agents are *actively* running — drives the working
        // dot pulse. Strips at terminal status still show but don't
        // count as busy.
        const CHAIN_RUNNING = new Set(['queued', 'running']);
        const DOC_RUNNING = new Set(['Queued', 'Processing', 'Extracting']);
        const chainAgentWorking = computed(() =>
            panelChains.value.some((c) => CHAIN_RUNNING.has(c.status)),
        );
        const docAgentWorking = computed(() =>
            panelDocCreations.value.some((d) => DOC_RUNNING.has(d.status)),
        );
        const agentsAnyWorking = computed(
            () => chainAgentWorking.value || docAgentWorking.value,
        );

        // Outer + per-agent collapse state. Outer auto-reopens when
        // the section reappears from empty (so a new chain starting
        // after the user collapsed an empty section isn't silently
        // hidden). Per-agent panels auto-reopen when their pane FIRST
        // appears in the session — subsequent collapses are respected
        // until that pane empties + a new batch arrives.
        const agentsSectionOpen = ref(true);
        const chainPanelOpen = ref(true);
        const docPanelOpen = ref(true);
        watch(agentsSectionVisible, (now, prev) => {
            if (now && !prev) agentsSectionOpen.value = true;
        });
        watch(
            () => panelChains.value.length,
            (now, prev) => {
                if (now > 0 && (prev ?? 0) === 0) chainPanelOpen.value = true;
            },
        );
        watch(
            () => readyToCreateList.value.length + panelDocCreations.value.length,
            (now, prev) => {
                if (now > 0 && (prev ?? 0) === 0) docPanelOpen.value = true;
            },
        );

        /**
         * Outer-header summary — reads like a tiny log line so the
         * user can tell at a glance what's running without expanding.
         */
        const agentsSummary = computed(() => {
            const parts = [];
            if (panelChains.value.length > 0) {
                parts.push(
                    `${panelChains.value.length} chain${panelChains.value.length === 1 ? '' : 's'}`,
                );
            }
            if (readyToCreateList.value.length > 0) {
                parts.push(`${readyToCreateList.value.length} queued`);
            }
            if (panelDocCreations.value.length > 0) {
                parts.push(
                    `${panelDocCreations.value.length} doc${panelDocCreations.value.length === 1 ? '' : 's'}`,
                );
            }
            return parts.length === 0 ? 'Idle' : parts.join(' · ');
        });

        /**
         * Doc-card collapsed-header label — disambiguates queued-only,
         * active-only, and mixed states so the user can see the
         * breakdown without expanding.
         */
        const docPanelCountLabel = computed(() => {
            const queued = readyToCreateList.value.length;
            const active = panelDocCreations.value.length;
            if (queued > 0 && active === 0) return `${queued} queued`;
            if (queued > 0 && active > 0) return `${active} active · ${queued} queued`;
            return `${active} ${active === 1 ? 'doc' : 'docs'}`;
        });

        // ── Previous conversations for current page ──────────
        //
        // Hides the panel when the current page surface isn't tagged
        // (today: only documents) and when the user has no prior
        // conversations against the current ref. The collapsible state
        // is per-mount so a fresh open of the flyout starts expanded —
        // small list, no surprise.
        const historyOpen = ref(false);
        const contextFilter = computed(() => deriveContextFilter(pageContext.value));
        const previousConversations = computed(() => {
            const list = chat.contextConversations.value || [];
            const currentId = chat.conversationId.value;
            return list.filter((c) => c.conversation_id !== currentId);
        });
        watch(
            contextFilter,
            (filter) => {
                chat.loadConversationsForContext(filter).catch(() => {});
            },
            { immediate: true },
        );

        function onSwitchConversation(conversationId) {
            if (!conversationId) return;
            chat.switchConversation(conversationId).catch((err) => {
                console.error('[AtlasFlyout] switchConversation failed', err);
            });
        }

        /**
         * Friendly relative-or-absolute timestamp for the history row.
         * Today shows time, this week shows weekday, older shows date.
         * Returns empty string for unparseable inputs rather than NaN.
         */
        function formatHistoryDate(value) {
            if (!value) return '';
            const ts = Date.parse(value);
            if (!Number.isFinite(ts)) return '';
            const date = new Date(ts);
            const now = new Date();
            const sameDay =
                date.getFullYear() === now.getFullYear() &&
                date.getMonth() === now.getMonth() &&
                date.getDate() === now.getDate();
            if (sameDay) {
                return date.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
            }
            const dayMs = 24 * 60 * 60 * 1000;
            if (now.getTime() - ts < 7 * dayMs) {
                return date.toLocaleDateString([], { weekday: 'short' });
            }
            return date.toLocaleDateString([], { month: 'short', day: 'numeric' });
        }

        /**
         * Jump from an AgentsPanel chip ("View output") to the
         * corresponding assistant message in the flyout's scroller.
         * Brief highlight pulse so the eye lands. Mirrors what the
         * full-page chat does.
         */
        function onJumpToMessage(messageId) {
            if (!messageId) return;
            nextTick(() => {
                const scroller = scrollerRef.value;
                const el = scroller?.querySelector(
                    `[data-message-id="${messageId}"]`,
                );
                if (!el) return;
                el.scrollIntoView({ behavior: 'smooth', block: 'center' });
                el.classList.add('atlas-flyout__message-jump');
                window.setTimeout(() => {
                    el.classList.remove('atlas-flyout__message-jump');
                }, 1600);
            });
        }

        return {
            chat,
            open,
            suppressed,
            globalHidden,
            pageContext,
            close,
            messages,
            composing,
            chatStatus,
            chatError,
            uploads,
            contextUsage,
            usageAllowance,
            isBudgetBlocked,
            allowanceNotice,
            scrollerRef,
            flyoutWidth,
            resizing,
            onResizeStart,
            resetWidth,
            resizerGripY,
            resizerLineStyle,
            onResizerTrack,
            onResizerLeave,
            topOffset,
            hasPortal,
            inputPlaceholder,
            streamingText,
            scrollToBottom,
            onScrollerScroll,
            showJumpToLatest,
            jumpToLatestLabel,
            readyEntryFor,
            isDocCreationInProgress,
            onRefClick,
            onFollowUp,
            parkedWorkflow,
            onResumeWorkflow,
            onRetry,
            onCreateDocument,
            onUploadFiles,
            // Atlas Agents collapsibles:
            activeChains,
            liveChain,
            panelChains,
            inChatChains,
            inChatDocCreations,
            pendingHandoff,
            activeDocCreations,
            panelDocCreations,
            readyToCreateList,
            agentsSectionVisible,
            agentsSectionOpen,
            chainPanelOpen,
            docPanelOpen,
            chainAgentWorking,
            docAgentWorking,
            agentsAnyWorking,
            agentsSummary,
            docPanelCountLabel,
            onJumpToMessage,
            // Previous-conversations panel:
            historyOpen,
            contextFilter,
            previousConversations,
            onSwitchConversation,
            formatHistoryDate,
            // ChatMessage action forwarding:
            onCompareDocuments,
            onCompareLevels,
            openDocumentSummary,
            fallbackSummary,
            onOpenDocument,
            defaultWorkflowKey,
            canStartContextWorkflow,
            onStartContextWorkflow,
            handoffStrip,
            onSummaryOpenDocument,
            // Page-action chip:
            activeAction,
            respondToAction,
            // Per-page controls rendered above the input bar:
            pageControls,
        };
    },
};
</script>

<style scoped>
/*
 * Single-root wrapper. `display: contents` so the wrapper itself
 * contributes nothing to layout — the flyout's `position: fixed` and
 * the dialog's body-level portal still anchor exactly where they did
 * before the wrapper existed.
 */
.atlas-flyout-root {
    display: contents;
}

/*
 * Fallback summary popover: stacking above the flyout is handled by
 * DocViewPopUp itself (its fixed floater carries z-index 2500 > the
 * flyout's 2400) — the class remains as a styling hook only.
 */

/*
 * Slide-in from the right. Width-preserving translate so the content
 * doesn't reflow mid-transition.
 */
.atlas-flyout-slide-enter-active,
.atlas-flyout-slide-leave-active {
    transition: transform 0.22s ease, opacity 0.22s ease;
}
.atlas-flyout-slide-enter,
.atlas-flyout-slide-leave-to {
    transform: translateX(16px);
    opacity: 0;
}

/*
 * Position fixed. z-index 2400 sits above Vuetify's v-dialog overlay
 * (default ~200) so the flyout stays visible when a document modal
 * is open. `top: 56px` matches Vuetify's default app-bar height —
 * adjust if the rm-portal header is a different size.
 */
.atlas-flyout {
    position: fixed;
    /* `top` is set inline via `:style` — driven by the topOffset ref
       which measures the topmost visible toolbar (main app bar OR
       a fullscreen v-dialog's toolbar) so the flyout sits flush
       against whichever surface is on top. */
    right: 0;
    bottom: 0;
    z-index: 2400;
    display: flex;
    flex-direction: column;
    background: #ffffff;
    border-left: 1px solid #e5e7eb;
    box-shadow:
        0 25px 50px -12px rgba(0, 0, 0, 0.25),
        0 0 0 1px rgba(0, 0, 0, 0.04);
    /* Smooth `top` transitions when a modal opens/closes — keeps the
       layout shift readable rather than a jarring snap. */
    transition: top 0.2s ease;
}

@media (max-width: 640px) {
    /* Mobile: stretch full width — narrow flyout is unusable on phones. */
    .atlas-flyout {
        width: 100% !important;
    }
}

/*
 * Inline mode — host page embeds the flyout inside its own layout.
 * Drop fixed positioning, the brand drop-shadow, and the left border
 * so the panel sits flush inside its container. The host is
 * responsible for giving the parent element `position: relative` and a
 * bounded height.
 */
.atlas-flyout--inline {
    position: absolute;
    top: 0;
    right: 0;
    bottom: 0;
    left: 0;
    width: 100% !important;
    z-index: auto;
    box-shadow: none;
    border-left: none;
    transition: none;
}

/*
 * Resize-active modifier. Suspends the width transition so the flyout
 * tracks the cursor exactly during a drag, and disables pointer events
 * on inner content so text-selection / button-hover don't interrupt
 * the drag. The handle itself stays interactive so a smooth release
 * is possible.
 */
.atlas-flyout--resizing {
    user-select: none;
    transition: none !important;
}
.atlas-flyout--resizing * {
    pointer-events: none;
}
.atlas-flyout--resizing .atlas-flyout__resizer {
    pointer-events: auto;
}

/*
 * Left-edge drag handle. Composed of three layers:
 *   - .atlas-flyout__resizer        the invisible 12px hit target
 *   - .atlas-flyout__resizer-line   a hairline separator at the seam
 *   - .atlas-flyout__resizer-pill   centred grip pill that fades in
 *                                   on hover, indigo on active drag
 * `col-resize` cursor telegraphs draggability; double-click resets.
 */
.atlas-flyout__resizer {
    position: absolute;
    left: -6px;
    top: 0;
    bottom: 0;
    width: 12px;
    cursor: col-resize;
    z-index: 2;
}
/* Hairline: a short gradient segment centred on the cursor (top/height
   set inline from `resizerLineStyle`), fading in on hover — the panel's
   own border-left carries the resting seam. */
.atlas-flyout__resizer-line {
    position: absolute;
    top: 0;
    height: 100%;
    left: 5px; /* centred inside the 12px hit area */
    width: 2px;
    border-radius: 2px;
    opacity: 0;
    background: linear-gradient(
        180deg,
        transparent,
        #6366f1 35%,
        #6366f1 65%,
        transparent
    );
    transition: opacity 0.18s ease;
}
.atlas-flyout__resizer:hover .atlas-flyout__resizer-line,
.atlas-flyout__resizer--active .atlas-flyout__resizer-line {
    opacity: 1;
}

/* Grip pill — follows the cursor along the rail (`top` set inline),
   scales up on hover. Absolutely positioned so it doesn't interfere
   with the hit target. */
.atlas-flyout__resizer-pill {
    position: absolute;
    left: 50%;
    transform: translate(-50%, -50%) scale(0.88);
    width: 14px;
    height: 52px;
    border-radius: 999px;
    background: #f3f4f6;
    border: 1px solid #d1d5db;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08);
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 3px;
    opacity: 0;
    transition:
        opacity 0.2s ease,
        transform 0.2s cubic-bezier(0.2, 0.9, 0.3, 1.2),
        background 0.18s ease,
        border-color 0.18s ease,
        box-shadow 0.18s ease;
}
/* Reveal on hover; bigger + indigo while dragging — the cubic-bezier
   gives the active state a slight spring so it feels "locked on". */
.atlas-flyout__resizer:hover .atlas-flyout__resizer-pill {
    opacity: 1;
    transform: translate(-50%, -50%) scale(1);
}
.atlas-flyout__resizer--active .atlas-flyout__resizer-pill {
    opacity: 1;
    transform: translate(-50%, -50%) scale(1.08);
    background: #6366f1;
    border-color: #4f46e5;
    box-shadow:
        0 0 0 4px rgba(99, 102, 241, 0.18),
        0 2px 8px rgba(79, 70, 229, 0.35);
}

/* Three short horizontal dashes inside the pill — "grip" affordance.
   White on active so they stay legible against the saturated indigo. */
.atlas-flyout__resizer-dash {
    width: 6px;
    height: 1.5px;
    border-radius: 2px;
    background: #6b7280;
    transition: background 0.18s ease;
}
.atlas-flyout__resizer--active .atlas-flyout__resizer-dash {
    background: #ffffff;
}

/* Header strip. */
.atlas-flyout__header {
    flex-shrink: 0;
    height: 48px;
    padding: 0 12px;
    display: flex;
    align-items: center;
    justify-content: space-between;
    border-bottom: 1px solid #e5e7eb;
}
.atlas-flyout__brand {
    display: flex;
    align-items: center;
    gap: 8px;
    min-width: 0;
}
/* Compass-in-disc mark (DS AtlasPanel) — the fixed Atlas indigo. */
.atlas-flyout__mark {
    flex-shrink: 0;
    width: 24px;
    height: 24px;
    border-radius: 50%;
    background: #4f46e5;
    display: flex;
    align-items: center;
    justify-content: center;
}
.atlas-flyout__brand-text {
    font-size: 13px;
    font-weight: 700;
    letter-spacing: -0.01em;
    color: #111827;
}
/* Context meter sits between the brand (left) and close (right); the
   header's space-between centres it. `margin: auto` keeps it centred even
   when the close button is hidden in inline mode. */
.atlas-flyout__meter {
    margin: 0 auto;
}
.atlas-flyout__close {
    width: 32px;
    height: 32px;
    padding: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 6px;
    color: #6b7280;
    background: transparent;
    border: 0;
    cursor: pointer;
    transition: background 0.15s ease, color 0.15s ease;
}
.atlas-flyout__close:hover {
    color: #111827;
    background: #f9fafb;
}
.atlas-flyout__close:focus-visible {
    outline: none;
    box-shadow: 0 0 0 3px rgba(99, 102, 241, 0.4);
}

/* Page-context chip. */
.atlas-flyout__context {
    flex-shrink: 0;
    padding: 8px 12px;
    display: flex;
    align-items: center;
    gap: 8px;
    border-bottom: 1px solid #e0e7ff;
    background: rgba(238, 242, 255, 0.7);
    font-size: 12px;
    min-width: 0;
}
.atlas-flyout__context-icon {
    color: #4f46e5;
    flex-shrink: 0;
}
.atlas-flyout__context-label {
    flex-shrink: 0;
    font-size: 10px;
    text-transform: uppercase;
    letter-spacing: 0.05em;
    font-weight: 500;
    color: #4338ca;
}
.atlas-flyout__context-summary {
    flex: 1 1 auto;
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    color: #374151;
}

/*
 * Messages region — relative-positioned wrapper that gives the
 * scroller `height: 100%` to fill the flexbox row, and parents the
 * floating "jump to latest" pill so the pill stays above the bottom
 * of the visible scroll area regardless of content height.
 */
.atlas-flyout__messages {
    flex: 1 1 auto;
    min-height: 0;
    position: relative;
}

/* Messages scroller — generous breathing room (DS AtlasChat). */
.atlas-flyout__scroller {
    height: 100%;
    overflow-y: auto;
    padding: 32px 20px 24px;
}

/* Thread column: capped at 42rem and centred, with roomy turn spacing,
   so a wide flyout reads as a document rather than a stretched chat. */
.atlas-flyout__thread {
    max-width: 42rem;
    width: 100%;
    margin: 0 auto;
    display: flex;
    flex-direction: column;
    gap: 28px;
}

/*
 * "Jump to latest" pill. Floats above the bottom of the scroll area;
 * shows when the user has scrolled out of the stick-to-bottom zone.
 * Indigo brand colour with a soft shadow so it lifts above the
 * messages without competing for attention. Force-scrolls on click.
 */
.atlas-flyout__jump-btn {
    position: absolute;
    bottom: 12px;
    left: 50%;
    transform: translateX(-50%);
    z-index: 10;
    display: inline-flex;
    align-items: center;
    gap: 6px;
    padding: 6px 12px;
    border: 0;
    border-radius: 999px;
    background: #4f46e5;
    color: #ffffff;
    font-size: 11px;
    font-weight: 600;
    cursor: pointer;
    box-shadow: 0 10px 15px -3px rgba(79, 70, 229, 0.25);
    transition: background 0.15s ease, transform 0.2s ease;
}
.atlas-flyout__jump-btn:hover {
    background: #4338ca;
}
.atlas-flyout-jump-fade-enter-active,
.atlas-flyout-jump-fade-leave-active {
    transition: opacity 0.2s ease, transform 0.2s ease;
}
.atlas-flyout-jump-fade-enter,
.atlas-flyout-jump-fade-leave-to {
    opacity: 0;
    transform: translate(-50%, 8px) scale(0.95);
}

/*
 * Previous-conversations panel. Sits directly below the context chip,
 * above the Atlas Agents collapsible. Capped at 30% of the flyout
 * height with internal scroll — long lists shouldn't crowd out the
 * chat area, but a few entries should render fully without forcing
 * a scrollbar.
 */
.atlas-flyout__history {
    flex-shrink: 0;
    border-bottom: 1px solid #e5e7eb;
    max-height: 30%;
    overflow-y: auto;
}
.atlas-flyout__history-toggle {
    width: 100%;
    padding: 8px 12px;
    display: flex;
    align-items: center;
    gap: 8px;
    background: transparent;
    border: 0;
    cursor: pointer;
    text-align: left;
    transition: background 0.15s ease;
}
.atlas-flyout__history-toggle:hover {
    background: rgba(0, 0, 0, 0.025);
}
.atlas-flyout__history-chevron {
    flex-shrink: 0;
    color: #6b7280;
}
.atlas-flyout__history-label {
    flex-shrink: 0;
    font-size: 10px;
    text-transform: uppercase;
    letter-spacing: 0.05em;
    font-weight: 500;
    color: #4f46e5;
}
.atlas-flyout__history-count {
    margin-left: auto;
    font-size: 11px;
    color: #6b7280;
}
.atlas-flyout__history-list {
    list-style: none;
    padding: 0 8px 8px;
    margin: 0;
    display: flex;
    flex-direction: column;
    gap: 4px;
}
.atlas-flyout__history-item {
    margin: 0;
    padding: 0;
}
.atlas-flyout__history-row {
    width: 100%;
    text-align: left;
    padding: 6px 8px;
    border: 1px solid transparent;
    border-radius: 6px;
    background: transparent;
    cursor: pointer;
    display: flex;
    flex-direction: column;
    gap: 2px;
    min-width: 0;
    transition: background 0.15s ease, border-color 0.15s ease;
}
.atlas-flyout__history-row:hover {
    background: rgba(238, 242, 255, 0.6);
    border-color: rgba(199, 210, 254, 0.7);
}
.atlas-flyout__history-title {
    font-size: 12px;
    font-weight: 600;
    color: #111827;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}
.atlas-flyout__history-preview {
    font-size: 11px;
    color: #6b7280;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}
.atlas-flyout__history-meta {
    font-size: 10px;
    color: #9ca3af;
    text-transform: uppercase;
    letter-spacing: 0.04em;
}

/*
 * Atlas Agents section — sits between the context chip and the
 * messages scroller. Hosts the outer "Atlas Agents" collapse plus
 * two independently-collapsible per-agent cards inside it. Capped
 * height with internal scroll so a pile of completed strips never
 * crowds out the chat area.
 */
.atlas-flyout__agents {
    flex-shrink: 0;
    border-bottom: 1px solid #e5e7eb;
    max-height: 40%;
    overflow-y: auto;
}

/* Outer "Atlas Agents" toggle — full-width row that stretches the
   length of the flyout. Subtle hover so it reads as clickable. */
.atlas-flyout__agents-toggle {
    width: 100%;
    padding: 8px 12px;
    display: flex;
    align-items: center;
    gap: 8px;
    background: transparent;
    border: 0;
    cursor: pointer;
    text-align: left;
    transition: background 0.15s ease;
}
.atlas-flyout__agents-toggle:hover {
    background: rgba(0, 0, 0, 0.025);
}
.atlas-flyout__agents-chevron {
    flex-shrink: 0;
    color: #6b7280;
}
.atlas-flyout__agents-chevron--chain {
    color: #6366f1;
}
.atlas-flyout__agents-chevron--doc {
    color: #059669;
}
.atlas-flyout__agents-label {
    flex-shrink: 0;
    font-size: 10px;
    text-transform: uppercase;
    letter-spacing: 0.05em;
    font-weight: 500;
    color: #4f46e5;
}
.atlas-flyout__agents-summary {
    font-size: 11px;
    color: #6b7280;
    flex: 1 1 auto;
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}

/* Working / idle dot indicator. Same dot is reused on the outer
   header and each per-agent card header, with colour modifier classes
   to match the card tone (indigo for chain, emerald for docs). */
.atlas-flyout__dot-indicator {
    flex-shrink: 0;
    width: 6px;
    height: 6px;
    border-radius: 50%;
}
.atlas-flyout__dot-indicator--working {
    background: #10b981;
    animation: atlas-flyout-dot-pulse 1.4s ease-in-out infinite;
}
.atlas-flyout__dot-indicator--idle {
    background: #9ca3af;
}
.atlas-flyout__dot-indicator--idle-chain {
    background: rgba(129, 140, 248, 0.6);
}
.atlas-flyout__dot-indicator--idle-doc {
    background: rgba(52, 211, 153, 0.6);
}
@keyframes atlas-flyout-dot-pulse {
    0%, 100% { opacity: 1; transform: scale(1); }
    50%      { opacity: 0.6; transform: scale(0.85); }
}

/* Outer body — wraps the per-agent cards inside the collapse. */
.atlas-flyout__agents-body {
    padding: 0 12px 12px;
    display: flex;
    flex-direction: column;
    gap: 8px;
}

/* Per-agent card — coloured by agent type so chain (indigo) and
   doc-creation (emerald) read as distinct surfaces. */
.atlas-flyout__agent-card {
    border-radius: 8px;
    overflow: hidden;
}
.atlas-flyout__agent-card--chain {
    border: 1px solid rgba(199, 210, 254, 0.7);
    background: rgba(238, 242, 255, 0.5);
}
.atlas-flyout__agent-card--doc {
    border: 1px solid rgba(167, 243, 208, 0.7);
    background: rgba(236, 253, 245, 0.5);
}

/* Per-agent toggle — same row layout as the outer toggle but
   smaller text and aligned to its card's tone. */
.atlas-flyout__agent-card-toggle {
    width: 100%;
    padding: 6px 8px;
    display: flex;
    align-items: center;
    gap: 8px;
    background: transparent;
    border: 0;
    cursor: pointer;
    text-align: left;
    transition: background 0.15s ease;
}
.atlas-flyout__agent-card--chain .atlas-flyout__agent-card-toggle:hover {
    background: rgba(199, 210, 254, 0.4);
}
.atlas-flyout__agent-card--doc .atlas-flyout__agent-card-toggle:hover {
    background: rgba(167, 243, 208, 0.4);
}
.atlas-flyout__agent-card-label {
    flex-shrink: 0;
    font-size: 10px;
    text-transform: uppercase;
    letter-spacing: 0.05em;
    font-weight: 500;
}
.atlas-flyout__agent-card-label--chain {
    color: #4338ca;
}
.atlas-flyout__agent-card-label--doc {
    color: #047857;
}
.atlas-flyout__agent-card-count {
    margin-left: auto;
    flex-shrink: 0;
    font-size: 10px;
}
.atlas-flyout__agent-card-count--chain {
    color: rgba(67, 56, 202, 0.7);
}
.atlas-flyout__agent-card-count--doc {
    color: rgba(4, 120, 87, 0.7);
}

/* The strips themselves render below their card's toggle. */
.atlas-flyout__agent-strips {
    padding: 4px 8px 8px;
    display: flex;
    flex-direction: column;
    gap: 6px;
}

/* Queued ready-to-create row — chain finished, user hasn't kicked
   off doc creation yet. Sits inside the doc card with Run + Dismiss
   controls so the user doesn't have to scroll to find the bubble's
   CTA. */
.atlas-flyout__queued {
    background: rgba(255, 255, 255, 0.7);
    border: 1px solid #a7f3d0;
    border-radius: 6px;
    padding: 6px 8px;
}
.atlas-flyout__queued-row {
    display: flex;
    align-items: center;
    gap: 8px;
    min-width: 0;
}
.atlas-flyout__queued-name {
    flex: 1 1 auto;
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    font-size: 12px;
    font-weight: 500;
    color: #1f2937;
}
.atlas-flyout__queued-status {
    flex-shrink: 0;
    font-size: 10px;
    text-transform: uppercase;
    letter-spacing: 0.05em;
    font-weight: 500;
    color: #047857;
}
.atlas-flyout__queued-actions {
    margin-top: 6px;
    display: flex;
    align-items: center;
    gap: 4px;
}
.atlas-flyout__queued-link {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    padding: 2px 6px;
    border: 0;
    background: transparent;
    cursor: pointer;
    font-size: 11px;
    color: #6b7280;
    border-radius: 4px;
}
.atlas-flyout__queued-link:hover {
    color: #111827;
}
.atlas-flyout__queued-actions-right {
    margin-left: auto;
    display: inline-flex;
    align-items: center;
    gap: 4px;
}
.atlas-flyout__queued-run {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    padding: 2px 8px;
    border: 0;
    border-radius: 4px;
    background: #059669;
    color: #ffffff;
    cursor: pointer;
    font-size: 11px;
    font-weight: 600;
    transition: background 0.15s ease;
}
.atlas-flyout__queued-run:hover:not(:disabled) {
    background: #047857;
}
.atlas-flyout__queued-run:disabled {
    opacity: 0.6;
    cursor: not-allowed;
}
.atlas-flyout__queued-dismiss {
    padding: 2px 6px;
    border: 0;
    background: transparent;
    cursor: pointer;
    font-size: 11px;
    color: #6b7280;
    border-radius: 4px;
}
.atlas-flyout__queued-dismiss:hover {
    color: #111827;
}

/* Composing turn — same mark + indented-body anatomy as a finished
   assistant message (DS AtlasChat), so the thread doesn't jump between
   two visual languages when a reply lands. */
.atlas-flyout__composing {
    display: block;
}
.atlas-flyout__composing-mark {
    display: flex;
    align-items: center;
    gap: 8px;
    margin-bottom: 8px;
}
.atlas-flyout__composing-name {
    font-size: 12px;
    font-weight: 600;
    color: #4b5563;
    letter-spacing: -0.01em;
}
.atlas-flyout__composing-content {
    min-width: 0;
    padding-left: 26px;
    display: flex;
    flex-direction: column;
    gap: 6px;
}
/* Tool-call pills. Stack inline like the full-page chat — wraps
   when there are several. */
.atlas-flyout__tool-pills {
    display: flex;
    flex-wrap: wrap;
    gap: 6px;
}
/* Streaming-cursor block: thin vertical bar that blinks at the end
   of the partial response so it visibly reads as live typing.
   Same colour token as the brand indigo so it ties to Atlas. */
.atlas-flyout__cursor {
    display: inline-block;
    margin-left: 2px;
    color: #4f46e5;
    animation: atlas-flyout-cursor-blink 0.9s steps(2, end) infinite;
}
@keyframes atlas-flyout-cursor-blink {
    50% { opacity: 0; }
}
/* Brief highlight pulse when the user clicks "View output" on an
   agents-panel strip — shared visual language with the full-page
   chat's message-jump highlight. */
.atlas-flyout__message-jump {
    animation: atlas-flyout-jump-pulse 1.6s ease-out;
    border-radius: 8px;
}
@keyframes atlas-flyout-jump-pulse {
    0%   { background-color: rgba(99, 102, 241, 0.12); }
    100% { background-color: transparent; }
}
/* Streaming text renders as plain prose, matching the finished
   assistant-turn treatment — no bubble. */
.atlas-flyout__composing-bubble {
    color: #4b5563;
    font-size: 15px;
    line-height: 1.65;
    max-width: 100%;
    min-width: 0;
}
.atlas-flyout__composing-thinking {
    display: flex;
    align-items: center;
    gap: 12px;
    color: #6b7280;
    font-size: 14px;
}
/* Live chain strip standing in for the thinking label. */
.atlas-flyout__composing-chain {
    max-width: 100%;
}
/* Workflow-owned chain running on after its turn — in-thread strip.
   The thread column's own gap + padding provide the separation now. */
.atlas-flyout__inchat-chain {
    margin: 0;
    max-width: 100%;
}
/* Handoff filler between a run finishing and its follow-on landing. */
.atlas-flyout__finalising {
    display: flex;
    align-items: center;
    gap: 8px;
    font-size: 12.5px;
    font-style: italic;
    color: rgb(100 116 139);
}
/* Thinking dots (DS AtlasChat): three 6px dots pulsing upward in
   sequence. Reduced-motion users get static dots at readable opacity. */
.atlas-flyout__dots {
    display: inline-flex;
    gap: 4px;
    color: #9ca3af;
}
.atlas-flyout__dot {
    width: 6px;
    height: 6px;
    border-radius: 9999px;
    background: currentColor;
    animation: atlas-flyout-pulse 1.2s cubic-bezier(0.4, 0, 0.2, 1) infinite;
}
.atlas-flyout__dot:nth-child(2) { animation-delay: 160ms; }
.atlas-flyout__dot:nth-child(3) { animation-delay: 320ms; }
@keyframes atlas-flyout-pulse {
    0%, 60%, 100% { opacity: 0.25; transform: translateY(0); }
    30%           { opacity: 1; transform: translateY(-2px); }
}
@media (prefers-reduced-motion: reduce) {
    .atlas-flyout__dot { animation: none; opacity: 0.6; }
}

/* Error banner. */
.atlas-flyout__error {
    display: flex;
    align-items: flex-start;
    gap: 8px;
    padding: 8px;
    border-radius: 6px;
    background: #fef2f2;
    border: 1px solid #fecaca;
    color: #b91c1c;
    font-size: 12px;
}

/* Monthly-allowance banner: amber warn, red when blocked. Sits between
   the messages and the controls strip, same visual family as __error. */
.atlas-flyout__allowance {
    flex-shrink: 0;
    display: flex;
    align-items: flex-start;
    gap: 8px;
    margin: 0 12px 8px 12px;
    padding: 8px;
    border-radius: 6px;
    background: #fffbeb;
    border: 1px solid #fde68a;
    color: #b45309;
    font-size: 12px;
}
/* Blocked = the whole chat is disabled: solid red so the flip from the
   tinted amber warn banner can't be missed. */
.atlas-flyout__allowance--blocked {
    background: #b91c1c;
    border-color: #991b1b;
    color: #ffffff;
    font-weight: 600;
}


/* Input bar. */
.atlas-flyout__input {
    flex-shrink: 0;
    padding: 8px 12px;
    border-top: 1px solid #e5e7eb;
}
/* Governance line under the composer (DS AtlasChat footer). */
.atlas-flyout__foot {
    margin: 8px 0 0;
    text-align: center;
    font-size: 11px;
    line-height: 1.4;
    color: #6b7280;
}

/*
 * Per-page controls row. Tight, low-chrome strip above the input —
 * page-scoped toggles/buttons that only make sense on the current
 * route. Wraps on narrow widths so multiple controls don't get clipped.
 */
.atlas-flyout__controls {
    flex-shrink: 0;
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 8px;
    padding: 6px 12px;
    border-top: 1px solid #e5e7eb;
    background: #f9fafb;
}
.atlas-flyout__control {
    font-family: inherit;
    font-size: 12px;
    color: rgb(55 65 81);
}
.atlas-flyout__control--toggle {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    cursor: pointer;
    user-select: none;
}
.atlas-flyout__control--toggle input[type='checkbox'] {
    cursor: pointer;
    accent-color: rgb(79 70 229);
}
.atlas-flyout__control--button {
    display: inline-flex;
    align-items: center;
    padding: 4px 10px;
    border-radius: 9999px;
    border: 1px solid rgb(209 213 219);
    background: white;
    cursor: pointer;
    transition: border-color 0.12s ease, background 0.12s ease;
}
.atlas-flyout__control--button:hover {
    border-color: rgb(129 140 248);
    background: rgb(238 242 255);
}

/* Parked-workflow chip row, directly above the input. */
.atlas-flyout__resume {
    flex-shrink: 0;
    display: flex;
    padding: 6px 12px 0;
}
.atlas-flyout__resume-btn {
    display: inline-flex;
    align-items: center;
    gap: 5px;
    padding: 4px 12px;
    border-radius: 9999px;
    border: 1px solid rgb(165 180 252);
    background: rgb(238 242 255);
    font-family: inherit;
    font-size: 12px;
    font-weight: 500;
    color: rgb(67 56 202);
    cursor: pointer;
    transition: border-color 0.12s ease, background 0.12s ease;
}
.atlas-flyout__resume-btn:hover {
    border-color: rgb(99 102 241);
    background: rgb(224 231 255);
}

/*
 * Page-action chip. Sits between messages and input. Two visual states:
 *   - pending   indigo-tinted background, prompts a decision
 *   - applied   emerald-tinted background, confirms result (and may
 *               surface a "Stop auto-applying" link)
 * The chip is a horizontal row that wraps on narrow widths so the
 * buttons drop below the description rather than being clipped.
 */
.atlas-flyout__action {
    flex-shrink: 0;
    padding: 10px 12px;
    display: flex;
    align-items: center;
    gap: 10px;
    flex-wrap: wrap;
    background: rgba(238, 242, 255, 0.7);
    border-top: 1px solid #e0e7ff;
}
.atlas-flyout__action-body {
    display: flex;
    align-items: center;
    gap: 8px;
    min-width: 0;
    flex: 1 1 220px;
}
.atlas-flyout__action-icon {
    color: #4f46e5;
    flex-shrink: 0;
}
.atlas-flyout__action-icon--applied {
    color: #059669;
}
.atlas-flyout__action-text {
    font-size: 12px;
    color: #1f2937;
    line-height: 1.35;
    min-width: 0;
    word-break: break-word;
}
.atlas-flyout__action-buttons {
    display: flex;
    align-items: center;
    gap: 6px;
    flex-shrink: 0;
}
.atlas-flyout__action-btn {
    height: 26px;
    padding: 0 10px;
    font-size: 12px;
    font-weight: 500;
    color: #374151;
    background: #ffffff;
    border: 1px solid #d1d5db;
    border-radius: 6px;
    cursor: pointer;
    transition: background 0.15s ease, border-color 0.15s ease, color 0.15s ease;
}
.atlas-flyout__action-btn:hover {
    background: #f3f4f6;
    border-color: #9ca3af;
}
.atlas-flyout__action-btn--primary {
    color: #ffffff;
    background: #4f46e5;
    border-color: #4338ca;
}
.atlas-flyout__action-btn--primary:hover {
    background: #4338ca;
    border-color: #3730a3;
}
.atlas-flyout__action-btn--ghost {
    width: 26px;
    padding: 0;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    color: #6b7280;
}
.atlas-flyout__action-link {
    font-size: 12px;
    color: #4f46e5;
    background: transparent;
    border: 0;
    padding: 0 4px;
    cursor: pointer;
    text-decoration: underline;
    text-underline-offset: 2px;
}
.atlas-flyout__action-link:hover {
    color: #4338ca;
}
/* Applied-state palette — softer green tint to read as "settled" vs.
   the indigo "pending" tint that the user still needs to decide on. */
.atlas-flyout__action:has(.atlas-flyout__action-icon--applied) {
    background: rgba(236, 253, 245, 0.75);
    border-top-color: #d1fae5;
}
</style>
